import { PathReporter } from "io-ts/lib/PathReporter"
import {
  AirportSeasonConfigurations,
  TAirportSeasonConfigurations
} from "./AirportSeasonConfiguration"
import callBackend, { callBackend1, OnFailure } from "./Communication"
import { Version, TVersion } from "./Version"
import { Countries, TCountries } from "./Countries"
import { Flights, TFlights } from "./Flight"
import { FlightReports, TFlightReportResult } from "./FlightReport"
import { ReasonCodes, TReasonCodes } from "./ReasonCodes"
import { Seasons, TSeasons } from "./Seasons"
import { ServiceTypes, TServiceTypes } from "./ServiceTypes"
import { TSlotAvailability } from "./SlotAvailability"
import { Transactions, TTransactions } from "./Transaction"
import { TUserInfo } from "./UserInfo"
import { AirportContacts, TAirportContacts } from "./AirportContacts"
import store from "../app/Store"
import { closeAllNotifications } from "../containers/notificationSlice"
import { DmpRecords, TGetDmpRecordsResult } from "./DmpRecord"

//#region Base data

export async function getVersion(
  pageId: string,
  onSuccess: (version: Version) => any
) {
  return callBackend1("getVersion", pageId, {}).then((r) => {
    if (r) {
      const dec = TVersion.decode(r)
      if (dec._tag === "Left") {
        console.error(PathReporter.report(dec))
      } else {
        onSuccess(dec.right)
      }
    }
  })
}

export async function getSeasons(
  pageId: string,
  onSuccess: (seasons: Seasons) => any
) {
  return callBackend1("getSeasons", pageId, {}).then((r) => {
    if (r) {
      const dec = TSeasons.decode(r.seasons)
      if (dec._tag === "Left") {
        console.error(PathReporter.report(dec))
      } else {
        onSuccess(dec.right)
      }
    }
  })
}

export async function getCountries(
  pageId: string,
  onSuccess: (countries: Countries) => any
) {
  return callBackend1("getCountries", pageId, {}).then((r) => {
    if (r) {
      const dec = TCountries.decode(r.countries)
      if (dec._tag === "Left") {
        console.error(PathReporter.report(dec))
      } else {
        onSuccess(dec.right)
      }
    }
  })
}

export async function getServiceTypes(
  pageId: string,
  onSuccess: (stList: ServiceTypes) => any
) {
  return callBackend1("getServiceTypes", pageId, {}).then((r) => {
    if (r) {
      const dec = TServiceTypes.decode(r.serviceTypes)
      if (dec._tag === "Left") {
        console.error(PathReporter.report(dec))
      } else {
        onSuccess(dec.right)
      }
    }
  })
}

export async function getAirportContacts(
  pageId: string,
  parameters: any,
  onSuccess: (contacts: AirportContacts) => any
) {
  return callBackend1("getAirportContacts", pageId, parameters).then((r) => {
    if (r) {
      const dec = TAirportContacts.decode(r.airportContacts)
      if (dec._tag === "Left") {
        console.error(PathReporter.report(dec))
      } else {
        onSuccess(dec.right)
      }
    }
  })
}

export async function getReasonCodes(
  pageId: string,
  onSuccess: (rcList: ReasonCodes) => any
) {
  return callBackend1("getReasonCodes", pageId, {}).then((r) => {
    if (r) {
      const dec = TReasonCodes.decode(r.reasonCodes)
      if (dec._tag === "Left") {
        console.error(PathReporter.report(dec))
      } else {
        onSuccess(dec.right)
      }
    }
  })
}

//#endregion

//#region Authentication

export async function getUserInfo(
  pageId: string,
  onSuccess: (userName: string) => any,
  onFailure: OnFailure
) {
  return callBackend1("getUserInfo", pageId, {}).then((r) => {
    if (r) {
      const dec = TUserInfo.decode(r)
      if (dec._tag === "Left") {
        console.error(PathReporter.report(dec))
        onFailure()
      } else if (dec.right.authStatus === "authenticated") {
        const userName = dec.right.userName
        onSuccess(userName)
      } else {
        onFailure()
      }
    }
  })
}

export async function login1(pageId: string, parameters: any) {
  store.dispatch(closeAllNotifications())
  return callBackend1("login1", pageId, parameters)
}

export async function login2(pageId: string, parameters: any) {
  store.dispatch(closeAllNotifications())
  return callBackend1("login2", pageId, parameters)
}

export async function login3(pageId: string, parameters: any) {
  store.dispatch(closeAllNotifications())
  return callBackend1("login3", pageId, parameters)
}

export async function loginUnlock(pageId: string, parameters: any) {
  return callBackend1("loginUnlock", pageId, parameters)
}

export async function loginReplace(pageId: string, parameters: any) {
  return callBackend1("loginReplace", pageId, parameters)
}

export async function requestUserPermissions(pageId: string) {
  return callBackend1("requestUserPermissions", pageId, {}).then((r) => {})
}

export async function logout(pageId: string) {
  return callBackend1("logout", pageId, {})
}
//#endregion

//#region Dynamic configuration

export async function getMenu(pageId: string) {
  return callBackend1("getMenu", pageId, {})
}

export async function getAirportSeasonConfigurations(
  pageId: string,
  onSuccess: (ascList: AirportSeasonConfigurations) => any,
  onFailure: OnFailure
) {
  return callBackend(
    "getAirportSeasonConfigurations",
    pageId,
    {},
    onFailure
  ).then((r) => {
    if (r) {
      const dec = TAirportSeasonConfigurations.decode(
        r.airportSeasonConfigurations
      )
      if (dec._tag === "Left") {
        console.error(PathReporter.report(dec))
        onFailure()
      } else {
        onSuccess(dec.right)
      }
    }
  })
}

//#endregion

//#region Flights

export async function saveFlightFilter(pageId: string, parameters: any) {
  return callBackend1("saveFlightFilter", pageId, parameters)
}

export async function getFlights(
  pageId: string,
  parameters: any,
  onSuccess: (flights: Flights) => any,
  onFailure: OnFailure
) {
  callBackend("getFlights", pageId, parameters, onFailure).then((r) => {
    if (r) {
      const dec = TFlights.decode(r.flights)
      if (dec._tag === "Left") {
        console.error(PathReporter.report(dec))
        onFailure()
      } else {
        onSuccess(dec.right)
      }
    }
  })
}

export async function refreshFlights(
  pageId: string,
  parameters: any,
  onSuccess: (flights: Flights) => void,
  onFailure: OnFailure
) {
  callBackend("refreshFlights", pageId, parameters, onFailure).then((r) => {
    if (r) {
      const dec = TFlights.decode(r.flights)
      if (dec._tag === "Left") {
        console.error(PathReporter.report(dec))
        onFailure()
      } else {
        onSuccess(dec.right)
      }
    } else {
      onFailure()
    }
  })
}

//#endregion

//#region availability

export async function getSlotAvailability(
  pageId: string,
  parameters: any,
  onSuccess: (html: string) => any,
  onFailure: OnFailure
) {
  callBackend("getSlotAvailability", pageId, parameters, onFailure).then(
    (r) => {
      if (r) {
        const dec = TSlotAvailability.decode(r.matrixAsHtml)
        if (dec._tag === "Left") {
          console.error(PathReporter.report(dec))
          onFailure()
        } else {
          onSuccess(dec.right)
        }
      }
    }
  )
}

export async function getFlightAvailability(
  pageId: string,
  parameters: any,
  onSuccessArr: (html: string) => void,
  onSuccessDep: (html: string) => void,
  onFailure: OnFailure
) {
  callBackend("getFlightAvailability", pageId, parameters, onFailure).then(
    (r) => {
      if (r) {
        if (r.arrMatrixAsHtml) {
          const arrMatrixDec = TSlotAvailability.decode(r.arrMatrixAsHtml)
          if (arrMatrixDec._tag === "Left") {
            console.error(PathReporter.report(arrMatrixDec))
            onFailure()
          } else {
            onSuccessArr(arrMatrixDec.right)
          }
        }
        if (r.depMatrixAsHtml) {
          const depMatrixDec = TSlotAvailability.decode(r.depMatrixAsHtml)
          if (depMatrixDec._tag === "Left") {
            console.error(PathReporter.report(depMatrixDec))
            onFailure()
          } else {
            onSuccessDep(depMatrixDec.right)
          }
        }
      }
    }
  )
}

//#endregion

//#region

export async function getFlightReports(
  pageId: string,
  parameters: any,
  onSuccess: (reports: FlightReports) => any,
  onFailure: OnFailure
) {
  callBackend("getFlightReports", pageId, parameters, onFailure).then((r) => {
    if (r) {
      const dec = TFlightReportResult.decode(r)
      if (dec._tag === "Left") {
        console.error(PathReporter.report(dec))
        onFailure()
      } else {
        onSuccess(dec.right.flightReports)
      }
    }
  })
}

export async function getFlightCountReport(
  pageId: string,
  parameters: any,
  onSuccess: (reports: FlightReports) => any,
  onFailure: OnFailure
) {
  callBackend("GetFlightCountReport", pageId, parameters, onFailure).then(
    (r) => {
      if (r) {
        const dec = TFlightReportResult.decode(r)
        if (dec._tag === "Left") {
          console.error(PathReporter.report(dec))
          onFailure()
        } else {
          onSuccess(dec.right.flightReports)
        }
      }
    }
  )
}

//#endregion

//#region Disable Message Processing

export async function getDmpRecords(
  pageId: string,
  parameters: any,
  onSuccess: (dmpRecords: DmpRecords) => any,
  onFailure?: OnFailure
) {
  callBackend("GetDmpRecords", pageId, parameters, onFailure).then((r) => {
    if (r) {
      const dec = TGetDmpRecordsResult.decode(r)
      if (dec._tag === "Left") {
        console.error(PathReporter.report(dec))
        onFailure()
      } else {
        onSuccess(dec.right.dmpRecords)
      }
    }
  })
}

export async function setDmpRecords(
  pageId: string,
  parameters: any,
  onSuccess: () => any,
  onFailure?: OnFailure
) {
  callBackend("SetDmpRecords", pageId, parameters, onFailure).then((r) => {
    if (r) {
      onSuccess()
    } else {
      onFailure()
    }
  })
}

//#endregion

//#region Transactions

export async function makeAddTransactions(
  pageId: string,
  parameters: any,
  onSuccess: () => void,
  onFailure: OnFailure
) {
  callBackend("MakeAddTransactions", pageId, parameters, onFailure).then(
    (r) => {
      if (r) {
        onSuccess()
      } else {
        onFailure()
      }
    }
  )
}

export async function makeEditTransaction(
  pageId: string,
  parameters: any,
  onSuccess: () => void,
  onFailure: OnFailure
) {
  callBackend("makeEditTransaction", pageId, parameters, onFailure).then(
    (r) => {
      if (r) {
        onSuccess()
      } else {
        onFailure()
      }
    }
  )
}

export async function saveTransactions(
  pageId: string,
  parameters: any,
  onSuccess: () => void,
  onFailure: OnFailure
) {
  callBackend("SaveTransactions", pageId, parameters, onFailure).then((r) => {
    if (r) {
      onSuccess()
    } else {
      onFailure()
    }
  })
}

export async function getTransactions(
  pageId: string,
  onSuccess: (transList: Transactions) => void,
  onFailure: OnFailure
) {
  callBackend("getTransactions", pageId, {}, onFailure).then((r) => {
    if (r) {
      const dec = TTransactions.decode(r.transactions)
      if (dec._tag === "Left") {
        console.error(PathReporter.report(dec))
        onFailure()
      } else {
        onSuccess(dec.right)
      }
    } else {
      onFailure()
    }
  })
}

export async function deleteAllTransactions(pageId: string) {
  const onSuccess = () => {}
  const onFailure = () => {}

  deleteTransactions(
    pageId,
    {
      transIds: [-1] // all transactions
    },
    onSuccess,
    onFailure
  )
}

export async function deleteTransactions(
  pageId: string,
  parameters: any,
  onSuccess: () => void,
  onFailure: OnFailure
) {
  callBackend("deleteTransactions", pageId, parameters, onFailure).then(
    (result) => {
      if (result) {
        onSuccess()
      } else {
        onFailure()
      }
    }
  )
}

export async function sendTransactions(
  pageId: string,
  parameters: any,
  onSuccess: (transactions: Transactions, infoMsg?: string) => void,
  onFailure: OnFailure
) {
  callBackend("sendTransactions", pageId, parameters, onFailure).then(
    (result) => {
      if (result) {
        onSuccess(result.transactions, result.message ?? null)
      } else {
        onFailure()
      }
    }
  )
}

//#endregion
